import { ValueObject } from '../shared/ValueObject';

/**
 * デバイス識別番号 値オブジェクト
 *
 * デバイスグループ番号（3桁）とデバイス番号（3桁）を連結した
 * 6桁のデバイス識別番号を表現する。
 *
 * 不変条件:
 * - deviceGroupNumber と deviceNumber がともに非nullの場合、各3桁ゼロ埋めで連結
 * - いずれかがnullの場合は「未取得」を表示
 */
export class DeviceIdentificationNumber extends ValueObject<string> {
  /** ゼロパディング桁数（デバイスグループ番号・デバイス番号共通） */
  private static readonly ZERO_PADDING_DIGIT_COUNT = 3;

  /** パディング文字 */
  private static readonly PADDING_CHARACTER = '0';

  /** デバイス識別番号が未設定時の表示テキスト */
  private static readonly UNSET_DISPLAY_TEXT = '未取得';

  private constructor(value: string) {
    super(value);
  }

  /**
   * デバイスグループ番号とデバイス番号からデバイス識別番号を生成
   *
   * 両方の値が非nullの場合のみ有効な識別番号を生成する。
   * いずれかがnullの場合は未設定状態の識別番号を返す。
   */
  public static create(params: {
    readonly deviceGroupNumber: number | null;
    readonly deviceNumber: number | null;
  }): DeviceIdentificationNumber {
    if (params.deviceGroupNumber === null || params.deviceNumber === null) {
      return new DeviceIdentificationNumber(
        DeviceIdentificationNumber.UNSET_DISPLAY_TEXT
      );
    }

    const formattedGroupNumber = String(params.deviceGroupNumber).padStart(
      DeviceIdentificationNumber.ZERO_PADDING_DIGIT_COUNT,
      DeviceIdentificationNumber.PADDING_CHARACTER,
    );
    const formattedDeviceNumber = String(params.deviceNumber).padStart(
      DeviceIdentificationNumber.ZERO_PADDING_DIGIT_COUNT,
      DeviceIdentificationNumber.PADDING_CHARACTER,
    );

    return new DeviceIdentificationNumber(
      `${formattedGroupNumber}${formattedDeviceNumber}`
    );
  }

  /**
   * 表示用文字列を取得
   */
  public toDisplayString(): string {
    return this.value;
  }

  public toString(): string {
    return this.value;
  }

  public toJSON(): string {
    return this.value;
  }

  /**
   * 他の DeviceIdentificationNumber と等しいかを判定
   */
  public equals(other: DeviceIdentificationNumber | null | undefined): boolean {
    if (!(other instanceof DeviceIdentificationNumber)) {
      return false;
    }
    return this.value === other.value;
  }
}
